import React, { useState, useEffect } from 'react'
import { Typography, Box, keyframes } from '@mui/material'

const pulse = keyframes`
  0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
  40% { opacity: 1; transform: scale(1); }
`

interface AnimatedLoadingTextProps {
  /** Array of messages to cycle through, or a single string */
  messages: string | string[]
  /** Milliseconds between message changes (default 3000) */
  interval?: number
  /** Font size (default '15px') */
  fontSize?: string
  /** Text color (default 'rgba(255,255,255,0.7)') */
  color?: string
}

export default function AnimatedLoadingText({
  messages,
  interval = 3000,
  fontSize = '15px',
  color = 'rgba(255,255,255,0.7)',
}: AnimatedLoadingTextProps): JSX.Element {
  const messageList = Array.isArray(messages) ? messages : [messages]
  const [index, setIndex] = useState(0)
  const [fade, setFade] = useState(true)

  useEffect(() => {
    if (messageList.length <= 1) return
    const timer = setInterval(() => {
      setFade(false)
      setTimeout(() => {
        setIndex((prev) => (prev + 1) % messageList.length)
        setFade(true)
      }, 300)
    }, interval)
    return () => clearInterval(timer)
  }, [messageList.length, interval])

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
      <Typography
        sx={{
          color,
          fontSize,
          fontWeight: 500,
          transition: 'opacity 0.3s ease',
          opacity: fade ? 1 : 0,
        }}
      >
        {messageList[index]}
      </Typography>
      <Box sx={{ display: 'flex', gap: '3px', ml: 0.5 }}>
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              backgroundColor: '#a855f7',
              animation: `${pulse} 1.4s infinite ease-in-out`,
              animationDelay: `${i * 0.16}s`,
            }}
          />
        ))}
      </Box>
    </Box>
  )
}
